/**
 * Shopify Cart Service
 * Handles all Shopify-related cart operations
 * Extracted from the main cart context for better separation of concerns
 */

import {
  createCart,
  updateCartQuantity,
  fetchCart,
  addToCart as shopifyAddToCart,
} from '../../lib/shopify';
import type { CartItem } from '../../app/hiv-test/types';

export interface ShopifyCart {
  id: string;
  checkoutUrl?: string | null;
}

export interface ShopifyLineItem {
  merchandiseId: string;
  quantity: number;
}

export interface UpdateLineItem {
  id: string;
  quantity: number;
}

export class ShopifyCartService {
  /**
   * Creates a new Shopify cart with the given line items
   */
  async createNewCart(
    shopifyLines: ShopifyLineItem[]
  ): Promise<ShopifyCart | null> {
    try {
      const newShopifyCart = await createCart(shopifyLines);

      if (newShopifyCart?.id) {
        return newShopifyCart;
      } else {
        console.error('Failed to create new cart');
        return null;
      }
    } catch (error) {
      console.error('Error creating new cart:', error);
      return null;
    }
  }

  /**
   * Updates an existing Shopify cart with new quantities
   */
  async updateExistingCart(
    cartId: string,
    shopifyLines: ShopifyLineItem[]
  ): Promise<ShopifyCart | null> {
    try {
      const fetchedCart = await fetchCart(cartId);

      if (!fetchedCart?.id) {
        console.error('Fetched cart is invalid');
        throw new Error('Invalid fetched cart');
      }

      // Map our current cart items to the lines in the Shopify cart
      const updateLines: UpdateLineItem[] = [];
      const addLines: ShopifyLineItem[] = [];

      // For each item in our local cart
      for (const item of shopifyLines) {
        // Try to find matching line in existing cart
        const existingLine = fetchedCart.lines?.edges?.find(
          (edge: { node: { id: string; merchandise: { id: string } } }) =>
            edge.node.merchandise.id === item.merchandiseId
        );

        if (existingLine) {
          updateLines.push({
            id: existingLine.node.id,
            quantity: item.quantity,
          });
        } else {
          addLines.push(item);
        }
      }

      let updatedCart = fetchedCart;

      // Update existing lines
      if (updateLines.length > 0) {
        updatedCart = await updateCartQuantity(cartId, updateLines);
      }

      // Add new lines
      if (addLines.length > 0) {
        updatedCart = await shopifyAddToCart(updatedCart.id, addLines);
      }

      return updatedCart;
    } catch (error) {
      console.error('Error updating existing cart:', error);
      throw error;
    }
  }

  /**
   * Syncs local cart with Shopify
   */
  async syncWithShopify(
    localCart: CartItem[],
    existingShopifyCart: ShopifyCart | null
  ): Promise<ShopifyCart | null> {
    try {
      const shopifyLines = localCart.map(item => ({
        merchandiseId: item.merchandiseId || item.id,
        quantity: item.quantity,
      }));

      // If we don't have an existing cart, create a new one
      if (!existingShopifyCart?.id) {
        return await this.createNewCart(shopifyLines);
      } else {
        // We have an existing cart - let's update it with the current quantities
        try {
          return await this.updateExistingCart(
            existingShopifyCart.id,
            shopifyLines
          );
        } catch (cartError) {
          console.error('Error updating existing cart:', cartError);

          // Cart might be expired or deleted on Shopify's end
          // Create a new cart
          return await this.createNewCart(shopifyLines);
        }
      }
    } catch (error) {
      console.error('Error syncing with Shopify:', error);
      return null;
    }
  }

  /**
   * Fetches a cart by ID
   */
  async fetchCartById(cartId: string): Promise<ShopifyCart | null> {
    try {
      const fetchedCart = await fetchCart(cartId);
      return fetchedCart || null;
    } catch (error) {
      console.error('Error fetching cart:', error);
      return null;
    }
  }

  /**
   * Validates that a cart has a checkout URL
   */
  validateCartCheckoutUrl(cart: ShopifyCart): boolean {
    if (!cart.checkoutUrl) {
      console.error('Cart missing checkoutUrl:', cart);
      return false;
    }
    return true;
  }
}
